import {
  Box,
  Button,
  Dialog,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useCreate, useInvalidate, useList, useUpdate } from '@refinedev/core'
import { UUID } from 'crypto'
import { useState, useEffect } from 'react'
import { User } from '@/utils/auth/auth0'

interface AssignUserFunderModalProps {
  open: boolean
  onClose: () => void
  funder_uuid: UUID | null
  currentUser: User
  currentAssignedUser: string | null
}

interface UserOption {
  uuid: UUID | null
  name: string
}
export default function AssignUserFunderModal({
  open,
  onClose: handleCloseDialog,
  funder_uuid,
  currentUser,
  currentAssignedUser,
}: AssignUserFunderModalProps) {
  const [selectedUser, setSelectedUser] = useState<string>('')
  const { mutate: createAssignUserFunder } = useCreate()
  const { mutate: updateAssignUserFunder } = useUpdate()
  const invalidate = useInvalidate()

  useEffect(() => {
    setSelectedUser(currentAssignedUser ?? '')
  }, [currentAssignedUser])

  const { data: availableUsers, isLoading } = useList({
    resource: 'assigned-funders',
    meta: {
      get_users: true,
    },
  })

  if (!funder_uuid) {
    return null
  }

  function handleEditAssignUser() {
    if (!currentUser || currentUser.sub === null) {
      return
    }

    if (!funder_uuid) {
      return
    }

    updateAssignUserFunder(
      {
        resource: 'assigned-funders',
        values: {
          assigned_to_user_uuid: selectedUser,
        },
        id: funder_uuid,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'assigned-funders',
            invalidates: ['all'],
          })
          invalidate({
            resource: 'funders',
            invalidates: ['all'],
          })

          handleCloseDialog()
        },
        onError: () => {
          alert('Error assigning user. Please try again.')
        },
      }
    )
  }

  function handleAddAssignUser() {
    if (selectedUser === '') {
      return
    }
    if (!currentUser || currentUser.sub === null) {
      return
    }

    if (!funder_uuid) {
      return
    }

    createAssignUserFunder(
      {
        resource: 'assigned-funders',
        values: {
          assigned_to_user_uuid: selectedUser,
          funder_uuid: funder_uuid,
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'funders',
            invalidates: ['all'],
          })
          handleCloseDialog()
        },
        onError: () => {
          alert('Error assigning user. Please try again.')
        },
      }
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Assign Someone to this funder{' '}
        </Typography>
        <Select
          sx={{ mb: 2, width: '100%' }}
          disabled={isLoading}
          value={selectedUser} // Use `selectedUser` instead
          label="User"
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {availableUsers &&
            (availableUsers.data as UserOption[])
              .filter((user: UserOption) => user.uuid !== null)
              .map((user: UserOption) => (
                <MenuItem key={user.uuid} value={String(user.uuid)}>
                  {user.name}
                </MenuItem>
              ))}
        </Select>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {currentAssignedUser ? (
            <Button onClick={() => handleEditAssignUser()}>Update</Button>
          ) : (
            <Button onClick={() => handleAddAssignUser()}>Save</Button>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}
